/*
Main component for the network itself, handling interaction with the ReactFlow library
Contains functions for updating the network when edges are added etc.

Somewhat scattered currently, but efficient. 
*/

/* eslint-disable no-unused-vars */
import React, { useCallback, useRef, useEffect, useState } from "react";
import ReactFlow, {
  addEdge,
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import "../css/custom-nodes.css";
import "../css/settings.css";
import CustomControls from "./CustomControls";
import CustomNodes from "./CustomNodes";
import CustomEdges from "./CustomEdges";
import FloatControls from "./FloatControls";
import {
  updateStates,
  calcBayesianStatsRecursive,
  insertValues,
  arrayDif,
} from "./Maths";
import {
  condProbNewParent,
  condProbDelParent,
  condProbNewState,
  condProbDelState,
} from "./ConditionalProbTable";
import { nodes as initialNodes, edges as initialEdges } from "./generator_new";
import usePrevious from "./usePrevious";
import {
  getDecimal,
  setDecimal,
  getEdgeDeleteButtonVisibility,
  setEdgeDeleteButtonVisibility,
  getDefaultNodeName,
  setUseSetStates,
  getUseSetStates,
  setNodeNamesOnly,
  getNodeNamesOnly,
  setShowProbabilities,
  getShowProbabilities,
  getEdgeType,
  setEdgeType,
} from "./UserSettings";
import { newNet, getRefreshNodes, setRefreshNodes } from "./App.js";
import { Alert, Space, Message } from "antd";

import FloatingEdge from "./FloatingEdge.js";
import FloatingConnectionLine from "./FloatingConnectionLine.js";

const nodeTypes = { customNode: CustomNodes };
const edgeTypes = {
  customEdge: FloatingEdge,
};
let flowInstance = null;
const onInit = (reactFlowInstance) => {
  console.log("flow loaded:", reactFlowInstance);
  flowInstance = reactFlowInstance;
};

function generateAlphanumericId() {
  // Generate a random alphanumeric ID
  while (true) {
    let id = Array(10)
      .fill(null)
      .map(() => Math.random().toString(36)[2])
      .join("");

    let isUnique = true;

    // Check if the new id is unique
    for (let i = 0; i < initialNodes.length; i++) {
      if (initialNodes[i].id === id) {
        isUnique = false;
        break;
      }
    }

    // Check if the ID contains at least one letter and one number
    if (/[a-z]/.test(id) && /[0-9]/.test(id) && isUnique) {
      // If the ID meets the requirements, return it
      return id;
    }
  }
}

function getParents(eds) {
  let parents = {};
  let parentsCount = 0;
  eds.forEach((edge) => {
    parentsCount += 1;
    const { source, target } = edge;
    if (!(target in parents)) {
      parents[target] = [];
    }
    parents[target].push(source);
  });
  return [parents, parentsCount];
}

//Given a node id, returns the parent data of that node
//NB throughout that curr refers to the node's parentData before updating
function getParentData(nodeID, edges, nodes) {
  const parents = getParents(edges)[0][nodeID];
  let currNode = nodes.find((node) => node.id === nodeID);
  const currParentData = JSON.parse(JSON.stringify(currNode.data.parentData));
  const currNumParents = Object.keys(currParentData).length;
  if (parents !== undefined) {
    const parentsStatesLabels = parents
      .map((p) => nodes.find((n) => n.id === p).data.states)
      .flat(Infinity);
    const currParentsStatesLabels = Object.values(currNode.data.parentData)
      .map((p) => Object.values(p)[0])
      .map((p) => Object.values(p)[0])
      .flat(Infinity);
    //Total number of states in the parents
    const numParentStates = parents
      .map((p) => nodes.find((node) => node.id === p).data.states.length)
      .reduce((a, b) => a + b);
    let numCurrParentStates = 0;
    if (
      Object.values(currParentData)
        .map((p) => Object.values(p)[0])
        .map((p) => Object.keys(p).length).length > 0
    ) {
      numCurrParentStates = Object.values(currParentData)
        .map((p) => Object.values(p)[0])
        .map((p) => Object.keys(p).length)
        .reduce((a, b) => a + b);
    }

    if (parents.length > currNumParents) {
      //New parent added
      let newParent;
      if (currNumParents === 0) {
        newParent = parents[0];
      } else {
        const currParents = Object.values(currParentData).map(
          (p) => Object.keys(p)[0]
        );
        newParent = parents.filter((p) => !currParents.includes(p))[0];
      }
      let newParentNode = nodes.find((node) => node.id === newParent);
      let newParentStates = newParentNode.data.states.map(
        (s) => Object.keys(s)[0]
      );
      let statesObj = {};
      for (let i = 0; i < newParentStates.length; i++) {
        statesObj[i + 1] = newParentStates[i];
      }
      let obj = {};
      obj[newParent] = statesObj;
      currParentData[parents.length.toString()] = obj;
      return currParentData;
    } else if (parents.length < currNumParents) {
      //Deleted parent
      const currParents = Object.values(currParentData).map(
        (p) => Object.keys(p)[0]
      );
      const delParent = currParents.filter((p) => !parents.includes(p))[0];
      let newParentData = {};
      let count = 1;
      for (let i = 0; i < currNumParents; i++) {
        if (currParents[i] !== delParent) {
          newParentData[count] = currParentData[i + 1];
          count += 1;
        }
      }
      return newParentData;
    }

    //New state/deleted in existing parent
    //The order of the states is always the order they appear in the parent
    //As such, we can just rebuild them from scratch
    else if (
      numParentStates !== numCurrParentStates ||
      JSON.stringify(parentsStatesLabels) !==
        JSON.stringify(currParentsStatesLabels)
    ) {
      let newParentData = JSON.parse(JSON.stringify(currParentData));
      for (let i = 0; i < currNumParents; i++) {
        const states = nodes.find(
          (node) => node.id === Object.keys(currParentData[i + 1])[0]
        ).data.states;
        let statesObj = {};
        for (let j = 0; j < states.length; j++) {
          statesObj[j + 1] = Object.keys(states[j])[0];
        }
        newParentData[i + 1][Object.keys(currParentData[i + 1])[0]] = statesObj;
      }
      return newParentData;
    } else {
      return currParentData;
    }
  }
  return {};
}

export function anyEvidence(nodes) {
  if (nodes === undefined) {
    return false;
  }
  let evidence = false;
  for (let i = 0; i < nodes.length; i++) {
    if (getEvidence(nodes[i].data.setStates) !== -1) {
      evidence = true;
      break;
    }
  }
  return evidence;
}

//Takes a node, returns the index of state that has evidence set - or -1 if no evidence
export function getEvidence(setStates) {
  if (setStates === undefined) {
    return -1;
  }
  let setStateValues = setStates.map((s) => Object.values(s)[0]);
  if (setStateValues.includes(1)) {
    return setStateValues.indexOf(1);
  }
  return -1;
}

//Converts a parentData object into the old structure, as is used in conditional probability tables and more
//Potentially only a short term solution
export function convertParentData(data) {
  //New parentData: parentData: {1: {"gen": {1: "operational", 2: "damaged"}}, 2: {"ant": {1: "operational", 2: "damaged"}}}
  //Old parentData: parentData: [{"gen": [{'operational': 0.4}, {'damaged': 0.6}]}, {"ant": [{'operational': 0.8}, {'damaged': 0.2}]}]
  let converted = [];
  let parents = Object.values(data);
  for (let i = 0; i < parents.length; i++) {
    let id = Object.keys(parents[i])[0];
    let states = Object.values(Object.values(parents[i])[0]);
    let statesArr = [];
    for (let j = 0; j < states.length; j++) {
      let stateObj = {};
      stateObj[states[j]] = 1;
      statesArr.push(stateObj);
    }
    let obj = {};
    obj[id] = statesArr;
    converted.push(obj);
  }
  return converted;
}

//function makeKeyValuePair(dictionary) that changes items from "fuel": {'operational': 0.4, 'damaged': 0.6} to "fuel": [{'operational': 0.4}, {'damaged': 0.6}]
function makeKeyValuePair(dictionary) {
  let newDict = {};
  let keys = Object.keys(dictionary);
  for (let i = 0; i < keys.length; i++) {
    let key = keys[i];
    let value = dictionary[key];
    let newValues = [];
    let valueKeys = Object.keys(value);
    for (let j = 0; j < valueKeys.length; j++) {
      let newKey = valueKeys[j];
      let newValue = {};
      newValue[newKey] = value[newKey];
      newValues.push(newValue);
    }
    newDict[key] = newValues;
  }
  return newDict;
}

function isGraphAcyclic(eds, source, target) {
  let edgePairs = eds.map((e) => [e.source, e.target]);
  let newEdgePairs = edgePairs.concat([[source, target]]);
  let Graph = require("graphlib").Graph;
  let isAcyclic = require("graphlib").alg.isAcyclic;
  let graph = new Graph();
  newEdgePairs.forEach((e) => graph.setEdge(e[0], e[1]));
  return isAcyclic(graph);
}

export let lastDeleted;

//Tried doing this with a function in the node data as with add state etc, but didn't work.
//Not a brilliant solution - gets the *labels* of parents of a node given its ID
export function getParentLabels(id) {
  let parentLabels = [];
  let node = nodesExt.find((n) => n.id === id);
  if (node !== undefined && Object.values(node.data.parentData).length > 0) {
    const parentIDs = Object.values(node.data.parentData).map(
      (p) => Object.keys(p)[0]
    );
    parentIDs.forEach((id) => {
      let parent = nodesExt.find((n) => n.id === id);
      if (parent !== undefined) {
        parentLabels.push(nodesExt.find((node) => node.id === id).data.label);
      }
    });
    return parentLabels;
  }
  return [];
}

//create your forceUpdate hook
export function useForceUpdate() {
  const [value, setValue] = useState(0); // integer state
  return () => setValue((value) => value + 1); // update state to force render
}

//External nodes and edges for use outside of OverviewFlow - not sure about best practises here but couldn't think of a better way
//Fine for reading but DO NOT EDIT these as a way of updating nodes and edges
export let nodesExt;
export let edgesExt;

let importBool = false;
let importFitViewBool = false;
let newNode = false;
function OverviewFlow({ importNodes, importEdges, setImport }) {
  const forceUpdate = useForceUpdate();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  nodesExt = nodes;
  edgesExt = edges;
  const statesData = JSON.stringify(nodes.map((node) => node.data.states));
  const nodesCondProb = JSON.stringify(nodes.map((node) => node.data.condProb));
  const parentData = {};
  const nodesHasEvi = JSON.stringify(
    nodes.map((node) => getEvidence(node.data.setStates))
  ); //Array of each node - -1 if no evidence, index of state if evidence
  for (let i = 0; i < nodes.length; i++) {
    let key = nodes[i].id;
    let value = convertParentData(nodes[i].data.parentData);
    parentData[key] = value;
  }
  const parentDataJSON = JSON.stringify(parentData);
  const parentDataPrev = usePrevious(parentData);
  const parentLabels = JSON.stringify(
    nodes.map((node) => getParentLabels(node.id))
  );

  const onConnect = (params) => addEdgeChecked(params);

  const [showmessage, setShowmessage] = useState(false);
  const [alertmessage, setAlertmessage] = useState("Alert message");
  const handleClose = () => {
    setShowmessage(false);
  };

  const showAlert = (alertMessage) => {
    setShowmessage(true);
    setAlertmessage(alertMessage);
  };

  //Returns true if the edge to be added is valid
  function edgeValid(eds, source, target) {
    let edgePairs = eds.map((e) => [e.source, e.target]);
    let nonRepeat = !edgePairs.some(
      (e) =>
        (e[0] === source && e[1] === target) ||
        (e[0] === target && e[1] === source)
    );
    let noSelfLoop = source !== target;
    let isAcyclic = isGraphAcyclic(eds, source, target);
    if (!nonRepeat) {
      showAlert("Edge Inavlid: This edge already exists!");
    }
    if (!noSelfLoop) {
      showAlert("Edge Invalid: A node cannot have an edge to itself!");
    }
    if (!isAcyclic) {
      showAlert("Edge Invalid: Adding this edge would create a cycle!");
    }
    return nonRepeat && noSelfLoop && isAcyclic;
  }

  //Ensure new edges are only added if they're valid:
  function addEdgeChecked(params) {
    if (edgeValid(edges, params.source, params.target)) {
      console.log(params);
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: "customEdge",
            markerEnd: { type: MarkerType.ArrowClosed, fill: "black" },
            interactionWidth: 200,
            data: {
              funcs: {
                delEdge: function (id) {
                  deleteEdge(id);
                },
              },
            },
          },
          eds
        )
      );
    }
  }

  function deleteEdge(id) {
    setEdges((eds) => eds.filter((e) => e.id !== id));
  }

  function deleteNode(id) {
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
    lastDeleted = id;
  }

  if (newNet && !importBool) {
    setNodes([]);
    setEdges([]);
    newNet = false;
  }

  //Imports if importNodes is not empty - For now importing an empty network will not work. But that's a bit niche
  if (importBool) {
    importBool = false;
    setImport([], []);
    importFitViewBool = true;
  } else if (
    importFitViewBool &&
    flowInstance.getNodes()[0].height !== undefined
  ) {
    importFitViewBool = false;
    flowInstance.fitView();
  } else if (importNodes.length > 0) {
    console.log("imported into flow");
    console.log(importNodes);
    setNodes(importNodes);
    setEdges(importEdges);

    importBool = true;
  }

  /*Populates all the nodes with the onClick functions if not already present
   * This should be a temporary measure - only neccesary for them to work with an initial nodes file
   */
  useEffect(() => {
    if (nodes.length > 0 && Object.keys(nodes[0].data.funcs)[0] === undefined) {
      setNodes((nds) =>
        nds.map((node) => {
          //You have to nest these function references in new functions - not sure why
          node = {
            ...node,
            data: {
              ...node.data,
              funcs: {
                setEvi: function (i) {
                  setEvi(node.id, i);
                },
                nodeDataReplace: function (id, data) {
                  nodeDataReplace(id, data);
                },
                deleteNode: function (id) {
                  deleteNode(id);
                },
              },
            },
          };
          return node;
        })
      );
    }
  });

  //As above, but for edges
  useEffect(() => {
    if (edges.length > 0 && Object.keys(edges[0].data.funcs)[0] === undefined) {
      setEdges((eds) =>
        eds.map((edge) => {
          edge = {
            ...edge,
            data: {
              ...edge.data,
              funcs: {
                delEdge: function (id) {
                  deleteEdge(id);
                },
              },
            },
          };
          return edge;
        })
      );
    }
  });

  function setEvi(id, i) {
    if (getUseSetStates()) {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === id) {
            let index = getEvidence(node.data.setStates);
            let newSetStates = JSON.parse(JSON.stringify(node.data.setStates));
            if (index !== -1) {
              //Evidence already set to this state - remove evidence
              newSetStates[index][Object.keys(newSetStates[index])[0]] = 0;
            }
            if (i !== index) {
              //New state to set evidence to
              newSetStates[i][Object.keys(newSetStates[i])[0]] = 1;
            }
            node = { ...node, data: { ...node.data, setStates: newSetStates } };
          }
          return node;
        })
      );
    }
  }

  function nodeDataReplace(id, newData) {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          newData.funcs = node.data.funcs;
          node = {
            ...node,
            data: {
              ...newData,
              states: newData.states,
              setStates: newData.setStates,
            },
          };
        }
        return node;
      })
    );
  }

  //Keeps parentData of each node up to date
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        node = {
          ...node,
          data: {
            ...node.data,
            parentData: getParentData(node.id, edges, nodes),
          },
        };
        return node;
      })
    );
  }, [edges, statesData, setNodes]);

  //Handles updates to the conditional probability table - interacts heavily with a set of helper functions
  //In future would be good to build a function to replace this that just converts directly from parentData to condProb
  useEffect(() => {
    let set = [];
    if (parentDataPrev !== undefined && nodes.length > 0) {
      setNodes((nds) =>
        nds.map((node) => {
          let parentDataOld = convertParentData(node.data.parentData);
          if (!set.includes(node.id)) {
            set.push(node.id);
            let pIndex;
            let addDel;
            if (parentDataPrev[node.id] !== undefined) {
              let pIndex = arrayDif(
                parentData[node.id].map((p) => Object.values(p)[0].length),
                parentDataPrev[node.id].map((p) => Object.values(p)[0].length)
              )[0]; //Index of changed parent in parentData
              let addDel = arrayDif(
                parentData[node.id].map((p) => Object.values(p)[0].length),
                parentDataPrev[node.id].map((p) => Object.values(p)[0].length)
              )[1]; //1 if adding, -1 if deleting

              let pDiff; //Number of new states
              if (
                parentData[node.id][pIndex] !== undefined &&
                parentDataPrev[node.id][pIndex] !== undefined
              ) {
                pDiff =
                  Object.values(parentData[node.id][pIndex])[0].length -
                  Object.values(parentDataPrev[node.id][pIndex])[0].length;
              }

              if (parentData[node.id].length > parentDataPrev[node.id].length) {
                //New parent
                node = {
                  ...node,
                  data: {
                    ...node.data,
                    condProb: condProbNewParent(
                      JSON.parse(JSON.stringify(node))
                    ),
                  },
                };
              } else if (
                parentData[node.id].length < parentDataPrev[node.id].length
              ) {
                //Delete parent
                node = {
                  ...node,
                  data: {
                    ...node.data,
                    condProb: condProbDelParent(
                      JSON.parse(JSON.stringify(node)),
                      parentDataPrev[node.id]
                    ),
                  },
                };
              } else if (pIndex !== -1 && addDel === 1) {
                //New state in existing parent
                for (let i = 0; i < pDiff; i++) {
                  node = {
                    ...node,
                    data: {
                      ...node.data,
                      condProb: condProbNewState(
                        JSON.parse(JSON.stringify(node)),
                        pIndex,
                        Object.values(parentDataOld[pIndex])[0].length
                      ),
                    },
                  };
                }
              } else if (pIndex !== -1 && addDel === -1) {
                //Delete state in existing parent
                node = {
                  ...node,
                  data: {
                    ...node.data,
                    condProb: condProbDelState(
                      JSON.parse(JSON.stringify(node)),
                      pIndex + 1,
                      lastDeleted + 1
                    ),
                  },
                };
              }
            }
          }
          return node;
        })
      );
    }
  }, [parentDataJSON, setNodes]);

  //This checks that the states in each node matches parentDatas.
  //Hacky fix to a problem I don't understand, parentData sometimes takes a while to update
  //But it wasn't a problem before - a mystery to me.
  let matchCheck = true;
  nodes.forEach((node) => {
    let parents = convertParentData(node.data.parentData);
    parents.forEach((parent) => {
      let id = Object.keys(parent)[0];
      let states = Object.values(parent)[0].map((s) => Object.keys(s)[0]);
      let nodeStored = nodes.find((n) => n.id === id);
      if (nodeStored === undefined) {
        return;
      }
      let storedStates = nodeStored.data.states.map((s) => Object.keys(s)[0]);

      if (JSON.stringify(storedStates) !== JSON.stringify(states)) {
        matchCheck = false;
        return;
      }
    });
  });
  //Updates the probabilities of each node in the network
  useEffect(() => {
    if (matchCheck && getShowProbabilities()) {
      let returnedStates = updateStates(nodes);
      returnedStates = makeKeyValuePair(returnedStates);
      setNodes((nds) =>
        nds.map((node) => {
          node = {
            ...node,
            data: { ...node.data, states: returnedStates[node.id] },
          };
          return node;
        })
      );
    }
  }, [
    nodesCondProb,
    nodesHasEvi,
    parentDataJSON,
    setNodes,
    getUseSetStates(),
    getDecimal(),
    getShowProbabilities(),
  ]);

  //Forces a re-render when a node label is changed, so that the label is updated in children nodes
  //Not an optimal solution
  useEffect(() => {
    refreshNodes();
  }, [parentLabels, setNodes]);

  //Called by button to add a new node to the nodes array, automatically updating the flow
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const addNode = useCallback(() => {
    let id = generateAlphanumericId();
    let viewport = document.getElementsByClassName("react-flow__renderer")[0];
    let newView = flowInstance.getViewport();
    viewport = viewport.getBoundingClientRect();

    let width = viewport.width;
    let height = viewport.height;
    let xy = flowInstance.project({ x: width, y: height });

    //Aproxximate width and height of the new node
    let nodeWidth = newView.zoom * 138.46;
    let nodeHeight = nodeWidth * 1.096;

    setNodes((_nodes) => [
      ...nodes,
      {
        id: id,
        type: "customNode",
        position: {
          x:
            (xy.x - newView.x / newView.zoom) / 2 -
            nodeWidth / 2 / newView.zoom,
          y: (xy.y - newView.y / newView.zoom) / 2 - nodeHeight / newView.zoom,
        },
        data: {
          label: getDefaultNodeName(),
          states: [{ Yes: 0.5 }, { No: 0.5 }],
          parentData: [],
          setStates: [{ S1: 0 }, { S2: 0 }],
          condProb: [{ S1: 0.5 }, { S2: 0.5 }],
          funcs: {
            setEvi: function (i) {
              setEvi(id, i);
            },
            nodeDataReplace: function (id, data) {
              nodeDataReplace(id, data);
            },
            deleteNode: function (id) {
              deleteNode(id);
            },
          },
        },
      },
    ]);
    newNode = true;
  });

  //Allows the name of the node to be changed immediately after adding
  if (
    newNode &&
    document.getElementsByClassName("custom-node").length === nodesExt.length
  ) {
    //Find div with class "custom-node"
    let divs = document.getElementsByClassName("custom-node");
    let div = divs[divs.length - 1];

    //Node is last in array
    let curNode = nodes[nodes.length - 1];

    //Set h2 in div to be editable
    let h2 = div.getElementsByTagName("h2")[0];
    h2.setAttribute("contenteditable", "true");
    h2.setAttribute("spellcheck", "false");

    //Disable new lines, have enter key blur
    h2.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        h2.blur();
      }
    });

    //Select the text in the h2
    let range = document.createRange();
    range.selectNodeContents(h2);
    let sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    h2.focus();

    //Select the text even more
    setTimeout(() => {
      document.execCommand("selectAll", false, null);
    }, 100);

    //After unselecting the text, set the label to the new text
    h2.addEventListener("blur", () => {
      h2.setAttribute("contenteditable", "false");

      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === curNode.id) {
            node = { ...node, data: { ...node.data, label: h2.innerText } };
          }
          return node;
        })
      );
    });

    newNode = false;
  }

  // Refresh the nodes to update the setStates
  // TODO check if this is appropriate
  function refreshNodes() {
    setNodes((nds) =>
      nds.map((node) => {
        node = JSON.parse(JSON.stringify(node));
        return node;
      })
    );
    setEdges((eds) =>
      eds.map((edge) => {
        edge = JSON.parse(JSON.stringify(edge));
        return edge;
      })
    );
  }
  if (getRefreshNodes()) {
    refreshNodes();
    setRefreshNodes();
  }

  return (
    <>
      {showmessage && (
        <Space
          direction="vertical"
          style={{
            width: "100%",
            padding: "0 20%",
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          <Alert
            message={alertmessage}
            type="warning"
            closable
            afterClose={handleClose}
          />
        </Space>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={onInit}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        attributionPosition="top-right"
        deleteKeyCode={null}
        connectionLineComponent={FloatingConnectionLine}
      >
        <MiniMap
          nodeStrokeColor={(n) => {
            if (n.style?.background) return n.style.background;
            if (n.type === "input") return "#0041d0";
            if (n.type === "output") return "#ff0072";
            if (n.type === "default") return "#1a192b";

            return "#eee";
          }}
          nodeColor={(n) => {
            if (n.style?.background) return n.style.background;

            return "#fff";
          }}
          nodeBorderRadius={2}
        />
        {/*Functions for buttons in custom controls need to be passed as props from here! - would like to be able to move this out of App.js*/}
        <CustomControls />
        <Background color="#aaa" gap={16} />
      </ReactFlow>
      <FloatControls addNode={addNode} forceUpdateFlow={forceUpdate} />
    </>
  );
}

export default OverviewFlow;
